import { useCallback, useMemo } from 'react'

import ThumbUpIcon from '@heroicons/react/outline/esm/ThumbUpIcon.js'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Post } from '@tribeplatform/gql-client/types'
import {
  useAddReaction,
  useRemoveReaction,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { useDelayedDisclosure } from '@tribeplatform/react-ui-kit/hooks'
import { Popover } from '@tribeplatform/react-ui-kit/Popover'

import { IconEmoji } from '../../common/components/IconEmoji.js'
import { useLogin } from '../../common/hooks/useLogin.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { confirmActionOnLockedPost } from '../utils.js'

export const SingleReactionWithOptions = ({
  post,
  options,
  mainReactionText,
  mainReaction,
  showIcon = true,
  showText = true,
}: {
  post: Post
  mainReactionText: string
  mainReaction: string
  options: { [key: string]: string }
  showIcon?: boolean
  showText?: boolean
}) => {
  const intl = useI18n()
  const { mutate: removeReaction } = useRemoveReaction()
  const { mutate: addReaction } = useAddReaction()
  const { isLoggedIn, showLogin } = useLogin()

  const selectedReaction = useMemo(
    () => post.reactions?.find(r => options[r.reaction] && r.reacted),
    [post.reactions, options],
  )

  let disabled = false

  if (post && isLoggedIn) {
    const [canReact] = hasScopesPermission(post, ['addReaction'])
    if (!canReact || !post.authMemberProps.canReact) {
      disabled = true
    }
  }

  const onRemoveReaction = useCallback(
    async reaction => {
      if (!(await confirmActionOnLockedPost(intl)(post))) {
        return
      }
      removeReaction({
        postId: post.id,
        reaction,
      })
    },
    [removeReaction, post, intl],
  )

  const onAddReaction = useCallback(
    reaction => async e => {
      if (!isLoggedIn) {
        e.preventDefault()
        return showLogin()
      }
      if (!(await confirmActionOnLockedPost(intl)(post))) {
        return
      }
      addReaction({
        postId: post?.id,
        input: {
          reaction,
        },
      })
    },
    [post, addReaction, showLogin, isLoggedIn, intl],
  )

  const [active, { open, close }] = useDelayedDisclosure()

  const leadingIcon = selectedReaction ? (
    <IconEmoji text={selectedReaction.reaction} size="sm" />
  ) : (
    <ThumbUpIcon className="transition-transform group-hover:-rotate-6" />
  )

  const activator = (
    <Button
      variant="secondaryNeutral"
      className="group select-none"
      size="lg"
      fullWidth
      disabled={disabled}
      leadingIcon={showIcon ? leadingIcon : null}
      pressed={!!selectedReaction}
      onClick={e => {
        e.preventDefault()
        if (selectedReaction) {
          onRemoveReaction(selectedReaction.reaction)
        } else {
          onAddReaction(mainReaction)(e)
        }
      }}
      onTouchStart={() => {
        if (!disabled) {
          open()
        }
      }}
      onMouseEnter={() => {
        if (!disabled) {
          open()
        }
      }}
      onMouseLeave={close}
    >
      {showText && (
        <span className="truncate">
          {selectedReaction
            ? options[selectedReaction.reaction]
            : mainReactionText}
        </span>
      )}
    </Button>
  )

  return (
    <Popover
      activator={activator}
      open={active}
      onClose={close}
      placement="top"
      padding="sm"
      className="flex-1 flex group"
      wrapperClassName="flex-1 min-w-0"
      rounded
    >
      <Popover.Content onMouseEnter={open} onMouseLeave={close}>
        <div className="flex space-s-2">
          {Object.keys(options).map(id => (
            <button
              type="button"
              key={id}
              className="cursor-pointer w-12 h-12 flex justify-center items-center transform transition-transform hover:scale-125"
            >
              <IconEmoji
                text={id}
                onClick={event => {
                  event.stopPropagation()
                  close()
                  if (selectedReaction) {
                    onRemoveReaction(selectedReaction.reaction)
                  }
                  onAddReaction(id)(event)
                }}
                size="lg"
              />
            </button>
          ))}
        </div>
      </Popover.Content>
    </Popover>
  )
}
